// src/components/CarBooking/Components/Tabs/SolicitantesUnidadesOrgTab.jsx
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../Utils/Endpoints';
import { Models } from '../../Utils/ModelsCarBooking';

const TITLETAB = 'Unidades Organizacionales';
const endpoint = ENDPOINTS.solicitanteUnidadesOrganizacionales;
const endpointById = ENDPOINTS.solicitanteUnidadesOrganizacionalesById;
const endpointUnidadesOrg = ENDPOINTS.unidadesOrg;
const modeloUnidadesOrg = Models.unidadesOrg;

const normalize = Models.solicitanteUnidadesOrganizacionales;

export default function SolicitantesUnidadesOrgTab({ fkValue }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Estado Drag & Drop
    const [allUnits, setAllUnits] = useState([]);
    const [initialAssigned, setInitialAssigned] = useState([]);
    const [currentAssigned, setCurrentAssigned] = useState([]);
    const [dragOverColumn, setDragOverColumn] = useState(null);

    // Cargar datos
    const loadData = async () => {
        if (!fkValue) return;
        setLoading(true);
        try {
            const [resRel, resUnidades] = await Promise.all([
                axios.get(endpoint), // Trae todas las relaciones (idealmente filtradas por backend, pero filtramos aquí por ahora)
                axios.get(endpointUnidadesOrg),
            ]);

            const allUnidades = (resUnidades?.data || []).map(modeloUnidadesOrg);
            // Enriquecer nombre para mostrar mejor
            const allUnidadesEnriched = allUnidades.map(u => ({
                ...u,
                _nombreUI: `${u.nombreUnidadOrganizacional || u.nombreUnidadOrg || u.descripcion || ''} (${u.clave || ''})`
            }));

            setAllUnits(allUnidadesEnriched);

            const allRelaciones = (resRel?.data || []).map(normalize);
            // Filtrar solo las de este solicitante
            const misRelaciones = allRelaciones.filter(r => Number(r.idSolicitante) === Number(fkValue));

            setInitialAssigned(misRelaciones);
            setCurrentAssigned(misRelaciones);

        } catch (e) {
            console.error(e);
            alert(`Error al cargar ${TITLETAB}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [fkValue]);

    // --- Cálculos D&D ---

    const availableUnits = useMemo(() => {
        const assignedIds = new Set(currentAssigned.map(r => r.idUnidadOrganizacional));
        return allUnits.filter(u => !assignedIds.has(u.idUnidadOrganizacional));
    }, [allUnits, currentAssigned]);

    const enrichedAssigned = useMemo(() => {
        return currentAssigned.map(rel => {
            const unidad = allUnits.find(u => u.idUnidadOrganizacional === rel.idUnidadOrganizacional);
            return {
                ...rel,
                _nombreUI: unidad ? unidad._nombreUI : `Unidad #${rel.idUnidadOrganizacional}`,
            };
        });
    }, [currentAssigned, allUnits]);

    // --- Handlers D&D ---

    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (dragOverColumn !== targetColumn) {
            setDragOverColumn(targetColumn);
        }
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned' && source === 'available') {
            addLocal(item); // item es una Unidad Organizacional
        } else if (targetColumn === 'available' && source === 'assigned') {
            removeLocal(item); // item es una Relación
        }
    };

    const addLocal = (unidadItem) => {
        const exists = currentAssigned.some(r => r.idUnidadOrganizacional === unidadItem.idUnidadOrganizacional);
        if (exists) return;

        const newRel = {
            idSolicitanteUnidadesOrganizacionales: 0, // Nuevo
            idSolicitante: Number(fkValue),
            idUnidadOrganizacional: unidadItem.idUnidadOrganizacional,
            activo: true
        };
        setCurrentAssigned(prev => [...prev, newRel]);
    };

    const removeLocal = (relItem) => {
        setCurrentAssigned(prev => prev.filter(r => r.idUnidadOrganizacional !== relItem.idUnidadOrganizacional));
    };

    // --- Guardar ---

    const handleSave = async () => {
        if (!fkValue) return;
        setSaving(true);
        try {
            // 1. Identificar nuevos (los que no estaban en initial)
            // Comparamos por idUnidadOrganizacional
            const initialIds = new Set(initialAssigned.map(r => r.idUnidadOrganizacional));
            const toAdd = currentAssigned.filter(r => !initialIds.has(r.idUnidadOrganizacional));

            // 2. Identificar eliminados (estaban en initial y ya no en current)
            const currentIds = new Set(currentAssigned.map(r => r.idUnidadOrganizacional));
            const toRemove = initialAssigned.filter(r => !currentIds.has(r.idUnidadOrganizacional));

            const promises = [];

            // Add
            for (const item of toAdd) {
                const payload = {
                    idSolicitante: Number(fkValue),
                    idUnidadOrganizacional: item.idUnidadOrganizacional,
                    activo: true
                };
                promises.push(axios.post(endpoint, payload));
            }

            // Remove
            for (const item of toRemove) {
                if (item.idSolicitanteUnidadesOrganizacionales > 0) {
                    promises.push(axios.delete(endpointById(item.idSolicitanteUnidadesOrganizacionales)));
                }
            }

            await Promise.all(promises);
            await loadData(); // Recargar para actualizar IDs reales
            alert('Cambios guardados correctamente.');

        } catch (e) {
            console.error(e);
            alert('Error al guardar cambios.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem' }}>
            <div className="mini-topbar" style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={saving || loading}
                    style={{ opacity: (saving || loading) ? 0.6 : 1 }}
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>

            <div style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.5rem' }}>
                Arrastra las unidades organizacionales para asignarlas al solicitante.
            </div>

            {/* Drag & Drop Container */}
            <div className="dnd-container" style={{ flex: 1, minHeight: '300px', display: 'flex', gap: '1rem' }}>

                {/* Left: Available */}
                <div
                    className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'available')}
                    onDrop={(e) => handleDrop(e, 'available')}
                    style={{ flex: 1, border: '1px solid #ccc', borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}
                >
                    <div className="dnd-header" style={{ fontWeight: 'bold', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                        Unidades Disponibles {availableUnits.length > 0 && `(${availableUnits.length})`}
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {loading ? (
                            <div className="dnd-empty">Cargando...</div>
                        ) : availableUnits.length === 0 ? (
                            <div className="dnd-empty" style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', marginTop: '2rem' }}>
                                No hay más unidades disponibles
                            </div>
                        ) : (
                            availableUnits.map(unit => (
                                <div
                                    key={unit.idUnidadOrganizacional}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, unit, 'available')}
                                    style={{ background: '#fff', padding: '0.6rem', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.1)', cursor: 'grab', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <span>{unit._nombreUI}</span>
                                    <button
                                        type="button"
                                        onClick={() => addLocal(unit)}
                                        style={{ background: 'none', border: 'none', fontSize: '1.2em', color: '#28a745', cursor: 'pointer', padding: '0 5px' }}
                                        title="Agregar"
                                    >＋</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Right: Assigned */}
                <div
                    className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, 'assigned')}
                    onDrop={(e) => handleDrop(e, 'assigned')}
                    style={{ flex: 1, border: '1px solid #ccc', borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', background: '#fff' }}
                >
                    <div className="dnd-header" style={{ fontWeight: 'bold', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                        Unidades Asignadas {enrichedAssigned.length > 0 && `(${enrichedAssigned.length})`}
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {loading ? (
                            <div className="dnd-empty">Cargando...</div>
                        ) : enrichedAssigned.length === 0 ? (
                            <div className="dnd-empty" style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', marginTop: '2rem' }}>
                                Arrastra unidades aquí para asignarlas
                            </div>
                        ) : (
                            enrichedAssigned.map(rel => (
                                <div
                                    key={rel.idUnidadOrganizacional}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, rel, 'assigned')}
                                    style={{ background: '#f0f4ff', border: '1px solid #dbeafe', padding: '0.6rem', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)', cursor: 'grab', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <span>{rel._nombreUI}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeLocal(rel)}
                                        style={{ background: 'none', border: 'none', fontSize: '1.2em', color: '#dc3545', cursor: 'pointer', padding: '0 5px' }}
                                        title="Remover"
                                    >✕</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
